import React, { useEffect, useMemo, useState } from 'react';
import { api, ApiError, getUsuarioGuardado } from './api';
import { EstadoError } from './EstadoError';

const FORM_INICIAL = { username: '', password: '', rol: 'cajero', supervisor_pin: '', activo: true, quitar_pin: false, sucursal_id: '', permisos: {} };

const ROLES = [
  { value: 'cajero', nombre: 'Cajero', icono: '🛒', descripcion: 'Ventas y tareas operativas.', color: '#16a34a', fondo: '#f0fdf4' },
  { value: 'supervisor', nombre: 'Supervisor', icono: '🧭', descripcion: 'Autoriza operaciones y supervisa caja.', color: '#0284c7', fondo: '#f0f9ff' },
  { value: 'admin', nombre: 'Administrador', icono: '⚙️', descripcion: 'Gestiona la empresa y sus sucursales.', color: '#7e22ce', fondo: '#faf5ff' },
  { value: 'propietario', nombre: 'Propietario', icono: '👑', descripcion: 'Acceso consolidado y multiempresa.', color: '#b45309', fondo: '#fffbeb' },
];


const PERMISOS = [
  ['gestionar_stock', 'Inventarios y ajustes de stock'],
  ['gestionar_compras', 'Compras, pedidos y recepciones'],
  ['transferir_stock', 'Transferencias entre depósitos'],
  ['gestionar_precios', 'Costos y precios'],
  ['gestionar_promociones', 'Promociones'],
  ['gestionar_facturacion', 'Facturación electrónica'],
  ['ver_reportes', 'Reportes, IVA y rentabilidad'],
  ['gestionar_usuarios', 'Usuarios de la empresa'],
  ['gestionar_configuracion', 'Configuración e identidad visual'],
  ['gestionar_organizacion', 'Sucursales, depósitos y puntos de venta'],
  ['ver_consolidado', 'Consolidado general de empresas'],
];

const ROL_META = Object.fromEntries(ROLES.map((r) => [r.value, r]));
const requierePin = (rol) => ['supervisor', 'admin', 'propietario'].includes(String(rol).toLowerCase());

export default function Usuarios() {
  const sesion = getUsuarioGuardado() || {};
  const esPropietario = String(sesion.rol || '').toLowerCase() === 'propietario';
  const [usuarios, setUsuarios] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [errorCarga, setErrorCarga] = useState('');
  const [formulario, setFormulario] = useState(FORM_INICIAL);
  const [editandoId, setEditandoId] = useState(null);
  const [asignandoAccesoId, setAsignandoAccesoId] = useState(null);

  const rolesDisponibles = useMemo(
    () => (esPropietario ? ROLES : ROLES.filter((r) => r.value !== 'propietario')),
    [esPropietario]
  );

  const cargarDatos = async () => {
    setErrorCarga('');
    try {
      const [usuariosData, organizacion] = await Promise.all([
        api.get('/api/usuarios'),
        api.get('/api/organizacion/resumen'),
      ]);
      const listaSucursales = Array.isArray(organizacion?.sucursales) ? organizacion.sucursales : [];
      setUsuarios(Array.isArray(usuariosData) ? usuariosData : []);
      setSucursales(listaSucursales);
      setFormulario((prev) => ({ ...prev, sucursal_id: prev.sucursal_id || String(sesion.sucursal_id || listaSucursales[0]?.id || '') }));
    } catch (err) {
      setErrorCarga(err instanceof ApiError ? err.mensaje : 'No se pudieron cargar los usuarios.');
    }
  };

  useEffect(() => { cargarDatos(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => setFormulario((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const guardarUsuario = async (e) => {
    e.preventDefault();
    if (!formulario.username.trim()) return alert('El nombre de usuario es obligatorio.');
    if (!formulario.sucursal_id) return alert('Seleccioná una sucursal.');
    try {
      if (asignandoAccesoId) {
        await api.put(`/api/usuarios/${asignandoAccesoId}/contextos`, { contextos: [{ empresa_id: Number(sesion.empresa_id), sucursal_id: Number(formulario.sucursal_id), rol: formulario.rol, permisos: formulario.permisos || {}, activo: formulario.activo, es_predeterminado: false }] });
      } else if (editandoId) await api.put(`/api/usuarios/${editandoId}`, formulario);
      else await api.post('/api/usuarios', formulario);
      alert(asignandoAccesoId ? '✅ Acceso agregado a la sucursal.' : editandoId ? '✅ Acceso actualizado.' : '✅ Usuario o acceso guardado correctamente.');
      cancelarEdicion();
      await cargarDatos();
    } catch (err) {
      alert(`❌ ${err instanceof ApiError ? err.mensaje : 'Error de conexión.'}`);
    }
  };

  const prepararEdicion = (user) => {
    setFormulario({
      username: user.username,
      password: '',
      rol: String(user.rol || 'cajero').toLowerCase(),
      supervisor_pin: '',
      activo: user.activo !== false,
      quitar_pin: false,
      sucursal_id: String(user.sucursal_id || sesion.sucursal_id || ''),
      permisos: user.permisos || {},
    });
    setAsignandoAccesoId(null);
    setEditandoId(user.id);
  };

  const prepararNuevoAcceso = (user) => {
    const usadas = new Set(usuarios.filter((u) => Number(u.id) === Number(user.id) && u.activo !== false).map((u) => Number(u.sucursal_id)));
    const disponible = sucursales.find((s) => s.activo !== false && !usadas.has(Number(s.id)));
    if (!disponible) return alert('Este usuario ya tiene acceso a todas las sucursales de la empresa.');
    setFormulario({ ...FORM_INICIAL, username: user.username, rol: 'cajero', sucursal_id: String(disponible.id), permisos: {} });
    setEditandoId(null);
    setAsignandoAccesoId(user.id);
  };

  const cancelarEdicion = () => {
    setFormulario({ ...FORM_INICIAL, sucursal_id: String(sesion.sucursal_id || sucursales[0]?.id || '') });
    setEditandoId(null);
    setAsignandoAccesoId(null);
  };

  const reactivarAcceso = async (user) => {
    try {
      await api.put(`/api/usuarios/${user.id}`, {
        username: user.username,
        password: '',
        rol: String(user.rol || 'cajero').toLowerCase(),
        supervisor_pin: '',
        quitar_pin: false,
        activo: true,
        sucursal_id: String(user.sucursal_id),
        permisos: user.permisos || {},
      });
      alert(`✅ Acceso de ${user.username} reactivado en ${user.sucursal_nombre}.`);
      await cargarDatos();
    } catch (err) {
      alert(`❌ ${err instanceof ApiError ? err.mensaje : 'Error al reactivar el acceso.'}`);
    }
  };

  const eliminarUsuario = async (user) => {
    if (!window.confirm(`¿Eliminar definitivamente el acceso de "${user.username}" a ${user.sucursal_nombre}?\n\nLa cuenta y sus accesos en otros comercios permanecerán activos.`)) return;
    try {
      await api.delete(`/api/usuarios/${user.id}/contextos/${user.contexto_id}`);
      alert(`✅ Acceso de ${user.username} eliminado de ${user.sucursal_nombre}.`);
      await cargarDatos();
    } catch (err) {
      alert(`❌ ${err instanceof ApiError ? err.mensaje : 'Error al quitar el acceso.'}`);
    }
  };

  if (errorCarga) return <EstadoError mensaje={errorCarga} onReintentar={cargarDatos} />;

  return (
    <div style={{ padding: 20, backgroundColor: '#f8fafc', minHeight: '80vh' }}>
      <h2 style={{ color: '#0f172a', borderBottom: '2px solid #38bdf8', paddingBottom: 10, marginTop: 0 }}>👥 Usuarios por sucursal</h2>
      <p style={{ color: '#64748b', marginTop: -2 }}>Cada acceso se asigna a una empresa y sucursal. Un mismo usuario puede tener accesos diferentes.</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 1fr) 2fr', gap: 20, marginTop: 20 }}>
        <div style={panelStyle}>
          <h3 style={{ marginTop: 0, color: '#334155' }}>{asignandoAccesoId ? '➕ Agregar acceso a sucursal' : editandoId ? '✏️ Editar acceso' : '➕ Nuevo usuario'}</h3>
          <form onSubmit={guardarUsuario} style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
            <div><label style={labelStyle}>Nombre de usuario</label><input name="username" value={formulario.username} onChange={handleChange} style={inputStyle} required disabled={Boolean(asignandoAccesoId)} /></div>
            {!asignandoAccesoId && <div>
              <label style={labelStyle}>Contraseña {editandoId ? <span style={ayudaStyle}>(vacío = conservar)</span> : <span style={ayudaStyle}>(obligatoria solo si el usuario todavía no existe)</span>}</label>
              <input type="password" name="password" value={formulario.password} onChange={handleChange} style={inputStyle} />
            </div>}
            <div>
              <label style={labelStyle}>Sucursal</label>
              <select name="sucursal_id" value={formulario.sucursal_id} onChange={handleChange} style={inputStyle} disabled={Boolean(editandoId) && !asignandoAccesoId}>
                <option value="">Seleccionar...</option>
                {sucursales.map((s) => <option key={s.id} value={s.id}>{s.codigo ? `${s.codigo} · ` : ''}{s.nombre}</option>)}
              </select>
              {editandoId && !asignandoAccesoId && <div style={ayudaStyle}>La sucursal no cambia al editar; usá ➕ para sumar otra.</div>}
            </div>
            <div>
              <label style={labelStyle}>Rol</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {rolesDisponibles.map((rol) => {
                  const seleccionado = formulario.rol === rol.value;
                  return <button key={rol.value} type="button" onClick={() => setFormulario((p) => ({ ...p, rol: rol.value }))} style={{ padding: 12, borderRadius: 10, border: seleccionado ? `2px solid ${rol.color}` : '1px solid #cbd5e1', background: seleccionado ? rol.fondo : '#fff', textAlign: 'left', cursor: 'pointer' }}>
                    <div style={{ fontWeight: 700 }}>{rol.icono} {rol.nombre}</div><div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{rol.descripcion}</div>
                  </button>;
                })}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Permisos adicionales en esta sucursal</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: 10, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                {PERMISOS.map(([clave, etiqueta]) => <label key={clave} style={{ display: 'flex', gap: 7, alignItems: 'flex-start', fontSize: 12, color: '#475569' }}>
                  <input type="checkbox" checked={Boolean(formulario.permisos?.[clave])} onChange={(e) => setFormulario((p) => ({ ...p, permisos: { ...(p.permisos || {}), [clave]: e.target.checked } }))} />
                  <span>{etiqueta}</span>
                </label>)}
              </div>
              <div style={ayudaStyle}>Los administradores y propietarios ya tienen acceso por rol. Estos permisos permiten delegar tareas específicas.</div>
            </div>
            {requierePin(formulario.rol) && <div>
              <label style={labelStyle}>PIN de autorización {editandoId && <span style={ayudaStyle}>(vacío = conservar)</span>}</label>
              <input type="password" inputMode="numeric" name="supervisor_pin" value={formulario.supervisor_pin} onChange={handleChange} style={inputStyle} placeholder="4 a 8 dígitos" maxLength={8} />
              {editandoId && <label style={{ display: 'flex', gap: 8, marginTop: 8, fontSize: 13, color: '#64748b' }}><input type="checkbox" checked={formulario.quitar_pin} onChange={(e) => setFormulario((p) => ({ ...p, quitar_pin: e.target.checked }))} /> Quitar PIN actual</label>}
            </div>}
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 700, color: '#475569', fontSize: 14 }}><input type="checkbox" checked={formulario.activo} onChange={(e) => setFormulario((p) => ({ ...p, activo: e.target.checked }))} /> Acceso activo</label>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" style={{ flex: 1, ...primaryButton, background: editandoId ? '#f59e0b' : '#10b981' }}>{asignandoAccesoId ? '✅ Agregar acceso' : editandoId ? '💾 Guardar' : '✅ Crear usuario'}</button>
              {(editandoId || asignandoAccesoId) && <button type="button" onClick={cancelarEdicion} style={{ ...primaryButton, background: '#e2e8f0', color: '#475569' }}>Cancelar</button>}
            </div>
          </form>
        </div>

        <div style={panelStyle}>
          <h3 style={{ marginTop: 0, color: '#334155' }}>📋 Accesos habilitados</h3>
          <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#f1f5f9' }}><th style={thStyle}>Usuario</th><th style={thStyle}>Comercio / sucursal</th><th style={thStyle}>Rol</th><th style={thStyle}>Estado</th><th style={{ ...thStyle, textAlign: 'right' }}>Acciones</th></tr></thead>
            <tbody>
              {usuarios.map((u) => {
                const meta = ROL_META[String(u.rol).toLowerCase()] || ROL_META.cajero;
                return <tr key={u.contexto_id || `${u.id}-${u.sucursal_id}`} style={{ borderBottom: '1px solid #e2e8f0', background: u.activo === false ? '#fff7ed' : '#fff' }}>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>{u.username}</td><td style={tdStyle}><b>{u.empresa_nombre || sesion.empresa_nombre}</b><br/><small>{u.sucursal_nombre || u.sucursal_id}</small></td>
                  <td style={tdStyle}><span style={{ padding: '4px 8px', borderRadius: 12, fontSize: 12, fontWeight: 700, background: meta.fondo, color: meta.color }}>{meta.icono} {meta.nombre}</span></td>
                  <td style={tdStyle}><div style={{ color: u.activo === false ? '#dc2626' : '#15803d', fontWeight: 700, fontSize: 12 }}>{u.activo === false ? '⛔ Inactivo' : '✅ Activo'}</div>{requierePin(u.rol) && <div style={{ fontSize: 11, color: u.tiene_pin_supervisor ? '#7e22ce' : '#b45309', marginTop: 3 }}>{u.tiene_pin_supervisor ? '🔐 PIN configurado' : '⚠️ Sin PIN'}</div>}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{u.activo === false && <button onClick={() => reactivarAcceso(u)} style={iconButton} title="Reactivar este acceso">🔓</button>}<button onClick={() => prepararNuevoAcceso(u)} style={iconButton} title="Agregar acceso a otra sucursal">➕</button><button onClick={() => prepararEdicion(u)} style={iconButton} title="Editar">✏️</button><button onClick={() => eliminarUsuario(u)} style={iconButton} title="Eliminar solo este acceso">🗑️</button></td>
                </tr>;
              })}
              {!usuarios.length && <tr><td colSpan="5" style={{ textAlign: 'center', padding: 20 }}>No hay accesos cargados.</td></tr>}
            </tbody>
          </table></div>
        </div>
      </div>
    </div>
  );
}

const panelStyle = { backgroundColor: '#fff', padding: 20, borderRadius: 10, boxShadow: '0 4px 6px rgba(0,0,0,.05)', height: 'fit-content' };
const labelStyle = { display: 'block', fontWeight: 700, marginBottom: 5, color: '#475569', fontSize: 14 };
const ayudaStyle = { color: '#94a3b8', fontWeight: 400, fontSize: 12 };
const inputStyle = { width: '100%', padding: 10, borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 14, boxSizing: 'border-box' };
const thStyle = { padding: 12, textAlign: 'left', borderBottom: '2px solid #cbd5e1', color: '#334155' };
const tdStyle = { padding: 12, color: '#475569' };
const primaryButton = { color: '#fff', padding: 12, border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer' };
const iconButton = { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16, marginLeft: 8 };
